import { React, useState, useEffect } from "react";
import "./CreateReservation.css";
import AppWrapper from "../AppWrapper";
import CarCard from "./CarCard";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";
import { formatPrice } from "../../utility/funcs";
import { hoursOfDay } from "../../utility/GlobalVariables";
import CarCardLoadMore from "./CarCardLoadMore";
import {
  calculateReservationTotal,
  getReservationTime,
  getPriceLists,
} from "../../utility/ReservationUtilities";
import { SearchIcon } from "@shopify/polaris-icons";
import {
  Page,
  Layout,
  Text,
  Button,
  BlockStack,
  FormLayout,
  TextField,
  Select,
  InlineGrid,
  Modal,
  Card,
  Divider,
  Badge,
  Banner,
} from "@shopify/polaris";
import CreateReservationSummary from "./CreateReservationSummary";
import ClientSearch from "../CustomComponents/ClientSearch/ClientSearch";
import PopUpSearch from "../CustomComponents/PopUpSearch/PopUpSearch";
import PopUpAmount from "../CustomComponents/PopUpAmount/PopUpAmount";
import { blockMessage, fetchClientBlock } from "../../utility/clientBlocks";
import CarHolds, { HOLD_SELECT, holdTime, isHoldLive, timeLeft, useNow } from "./CarHolds";

import { useNavigate, useSearchParams } from "react-router-dom";

function CreateReservation() {
  const navigate = useNavigate();
  const { session } = UserAuth();
  const { branch } = useBranch();
  const [resDetails, setResDetails] = useState({
    fromDate: "",
    fromHours: "09:00",
    toDate: "",
    toHours: "09:00",
    segmentToSearch: 0,
    insurance: "premium",
    searchPhrase: "",
    carsToShow: undefined,
    license: "local",
    paymentMethod: "card",
    lastResults: false,
    selectedCar: {
      model: undefined,
      car: undefined,
    },
    details: undefined,
    missingDates: false,
    priceListId: null,
  });
  const [priceLists, setPriceLists] = useState([]);

  useEffect(() => {
    getPriceLists().then((arr) => {
      setPriceLists(arr);
      setResDetails((prev) =>
        prev.priceListId ? prev : { ...prev, priceListId: arr[0]?.id ?? null }
      );
    });
  }, []);

  const [customer, setCustomer] = useState({
    id: undefined,
    name: undefined,
    contact: undefined,
  });
  const [customerModal, setCustomerModal] = useState(false);
  const handleCustomerModal = (state) => {
    setCustomerModal(state);
  };

  const [company, setCompany] = useState({ id: undefined, name: undefined });

  // When a company is picked and no customer is selected yet, auto-fill with
  // the company's primary contact (or fall back to the first linked contact).
  // If a customer is already set, leave it alone.
  const selectCompany = async ({ id, name }) => {
    setCompany({ id, name });
    if (!id || customer?.id) return;
    const { data } = await supabase
      .from("company_contacts")
      .select("is_primary, customer:customers(id, first_name, last_name)")
      .eq("company_id", id)
      .order("is_primary", { ascending: false })
      .order("id")
      .limit(1);
    const pick = data?.[0]?.customer;
    if (pick?.id) {
      setCustomer({
        id: pick.id,
        name: `${pick.first_name || ""} ${pick.last_name || ""}`.trim(),
        contact: undefined,
      });
    }
  };
  // Blocklist state for whoever is currently selected. The DB trigger is the
  // real guard, but we look it up on selection so the user gets told *why*
  // before filling out the rest of the form.
  const [customerBlock, setCustomerBlock] = useState(null);
  const [companyBlock, setCompanyBlock] = useState(null);

  useEffect(() => {
    let stale = false;
    if (!customer?.id) {
      setCustomerBlock(null);
      return;
    }
    fetchClientBlock("customer", customer.id).then((b) => {
      if (!stale) setCustomerBlock(b?.blocked ? b : null);
    });
    return () => {
      stale = true;
    };
  }, [customer?.id]);

  useEffect(() => {
    let stale = false;
    if (!company?.id) {
      setCompanyBlock(null);
      return;
    }
    fetchClientBlock("company", company.id).then((b) => {
      if (!stale) setCompanyBlock(b?.blocked ? b : null);
    });
    return () => {
      stale = true;
    };
  }, [company?.id]);

  const blockWarnings = [
    customerBlock &&
      blockMessage({
        entityType: "customer",
        name: customer?.name,
        ...customerBlock,
      }),
    companyBlock &&
      blockMessage({
        entityType: "company",
        name: company?.name,
        ...companyBlock,
      }),
  ].filter(Boolean);

  const [bookingOrigin, setBookingOrigin] = useState("presencial");
  const [resNotes, setResNotes] = useState("");
  const [deliveryType, setDeliveryType] = useState("pickup");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryFee, setDeliveryFee] = useState(500);
  const [returnType, setReturnType] = useState("pickup");
  const [returnAddress, setReturnAddress] = useState("");
  const [returnFee, setReturnFee] = useState(500);
  const [companyModal, setCompanyModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [clientSearchModal, setClientSearchModal] = useState(false);

  // Apartado (car_holds) this form is continuing, if any. Its car stays
  // visible in the search (p_hold_id) and creating the reservation consumes it.
  const [activeHold, setActiveHold] = useState(null);
  const [holdModal, setHoldModal] = useState(false);
  const [holdLabel, setHoldLabel] = useState("");
  const [holdSaving, setHoldSaving] = useState(false);
  const [holdsRefresh, setHoldsRefresh] = useState(0);
  useNow(); // ticks the hold countdown
  const [holdHours, setHoldHours] = useState(8);
  useEffect(() => {
    supabase
      .from("company_settings")
      .select("hold_working_hours")
      .maybeSingle()
      .then(({ data }) => data && setHoldHours(Number(data.hold_working_hours)));
  }, []);
  const [searchParams] = useSearchParams();

  // Sublease state
  const [subleaseModal, setSubleaseModal] = useState(false);
  const [subleaseData, setSubleaseData] = useState({
    model_id: "",
    plate: "",
    vin: "",
    dailyPrice: "1000",
    deposit: "4000",
  });
  const [subleaseInfo, setSubleaseInfo] = useState(null);
  const [carModels, setCarModels] = useState([]);

  // Load car models once for the sublease selector
  useEffect(() => {
    supabase
      .from("car_models")
      .select("id, name, show_as, brand:car_brands(name)")
      .order("name")
      .then(({ data }) => {
        if (data) setCarModels(data);
      });
  }, []);

  // Largo Plazo specific
  const isLP = branch?.segment === "largo_plazo";
  const [lpIndefinido, setLpIndefinido] = useState(true);
  const [lpMonths, setLpMonths] = useState(12);
  const [invoicingGroups, setInvoicingGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const handleCompanyModal = (state) => {
    setCompanyModal(state);
  };

  // Load invoicing groups when company changes (LP only)
  useEffect(() => {
    if (!isLP || !company?.id) { setInvoicingGroups([]); return; }
    const fetchGroups = async () => {
      const { data } = await supabase
        .from("invoicing_groups")
        .select("id, name")
        .eq("company_id", company.id)
        .eq("is_active", true)
        .order("name");
      setInvoicingGroups(data || []);
      if (data?.length > 0) setSelectedGroup(String(data[0].id));
    };
    fetchGroups();
  }, [isLP, company?.id]);

  const handleChange = (value, id) => {
    setResDetails({
      ...resDetails,
      [id]: value,
      carsToShow: undefined,
      selectedCar: {
        model: undefined,
        car: undefined,
      },
      details: undefined,
    });
  };

  const handleChosenCar = async ({ car, model }) => {
    // Mutual exclusion: selecting a fleet car clears any sublease
    if (subleaseInfo) setSubleaseInfo(null);
    if (isLP) {
      // LP: calculate using normal pricing for 30+ days tier
      const estimatedDays = lpIndefinido ? 30 : lpMonths * 30;
      const details = await calculateReservationTotal(
        { days: estimatedDays, hours: 0 },
        model,
        "card",
        "base",
        "local",
        resDetails.priceListId
      );
      setResDetails({
        ...resDetails,
        selectedCar: { car, model },
        details: { ...details, deposit: 0, time: { days: estimatedDays, hours: 0 } },
      });
    } else {
      const { days, hours } = getReservationTime(
        resDetails.fromDate,
        resDetails.fromHours,
        resDetails.toDate,
        resDetails.toHours
      );
      const details = await calculateReservationTotal(
        { days, hours },
        model,
        resDetails.paymentMethod,
        resDetails.insurance,
        resDetails.license,
        resDetails.priceListId
      );
      setResDetails({
        ...resDetails,
        selectedCar: { car, model },
        details: details,
      });
    }
  };
  // Switching the price list keeps the selected car and just recalculates the
  // total against the new list (unlike insurance/payment, which re-pick the car).
  const handlePriceListChange = async (value) => {
    const newId = parseInt(value);
    const model = resDetails.selectedCar?.model;
    if (!model) {
      setResDetails((p) => ({ ...p, priceListId: newId }));
      return;
    }
    if (isLP) {
      const estimatedDays = lpIndefinido ? 30 : lpMonths * 30;
      const details = await calculateReservationTotal(
        { days: estimatedDays, hours: 0 },
        model,
        "card",
        "base",
        "local",
        newId
      );
      setResDetails((p) => ({
        ...p,
        priceListId: newId,
        details: { ...details, deposit: 0, time: { days: estimatedDays, hours: 0 } },
      }));
    } else {
      const { days, hours } = getReservationTime(
        resDetails.fromDate,
        resDetails.fromHours,
        resDetails.toDate,
        resDetails.toHours
      );
      const details = await calculateReservationTotal(
        { days, hours },
        model,
        resDetails.paymentMethod,
        resDetails.insurance,
        resDetails.license,
        newId
      );
      setResDetails((p) => ({ ...p, priceListId: newId, details }));
    }
  };

  const confirmSublease = () => {
    if (!subleaseData.model_id || !subleaseData.plate) {
      alert("Modelo y Placa son requeridos");
      return;
    }
    if (!resDetails.fromDate || (!isLP && !resDetails.toDate)) {
      alert("Primero selecciona las fechas");
      return;
    }
    const time = isLP
      ? { days: 30, hours: 0 }
      : getReservationTime(resDetails.fromDate, resDetails.fromHours, resDetails.toDate, resDetails.toHours) || { days: 1, hours: 0 };
    const daily = parseFloat(subleaseData.dailyPrice) || 0;
    const days = time.days || 1;
    const details = {
      dailyPrice: daily,
      hoursExtra: 0,
      // LP carries no deposit (matches owned-car LP); CP uses the entered deposit.
      deposit: isLP ? 0 : parseFloat(subleaseData.deposit) || 0,
      totalRent: daily * days,
      time,
    };
    const model = carModels.find((m) => String(m.id) === subleaseData.model_id);
    setSubleaseInfo({
      ...subleaseData,
      model_label: model
        ? `${model.brand?.name || ""} ${model.show_as || model.name}`.trim()
        : "",
    });
    setResDetails({
      ...resDetails,
      selectedCar: { car: null, model: null },
      details,
    });
    setSubleaseModal(false);
  };

  // Prefill the sublease daily price + deposit from our own tariff for the
  // chosen catalog model (still editable). Same pricing inputs as an owned car:
  // LP uses base/local 30-day; CP uses the picked insurance/payment/license.
  useEffect(() => {
    if (!subleaseModal || !subleaseData.model_id) return;
    let cancelled = false;
    (async () => {
      const time = isLP
        ? { days: 30, hours: 0 }
        : getReservationTime(
            resDetails.fromDate,
            resDetails.fromHours,
            resDetails.toDate,
            resDetails.toHours
          ) || { days: 1, hours: 0 };
      const details = isLP
        ? await calculateReservationTotal(time, subleaseData.model_id, "card", "base", "local", resDetails.priceListId)
        : await calculateReservationTotal(
            time,
            subleaseData.model_id,
            resDetails.paymentMethod,
            resDetails.insurance,
            resDetails.license,
            resDetails.priceListId
          );
      if (cancelled || !details || details.success === false) return;
      setSubleaseData((prev) => ({
        ...prev,
        dailyPrice: String(Math.round(details.dailyPrice || 0) || ""),
        deposit: isLP ? "0" : String(Math.round(details.deposit || 0) || ""),
      }));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subleaseData.model_id, subleaseModal]);

  const getCarCards = () => {
    var cards = resDetails.carsToShow.map((car) => (
      <CarCard
        key={car.id}
        car={car}
        warning={extWarnings[car.id]}
        selected={resDetails.selectedCar.car}
        onClick={({ car, model }) => {
          handleChosenCar({ car, model });
        }}
      />
    ));
    cards.push(
      <CarCardLoadMore
        key={"load_more_button"}
        onClick={() => {
          if (resDetails.lastResults) return;
          fetchAvailableCars();
        }}
        disabled={resDetails.lastResults}
      />
    );
    return cards;
  };
  // carId → "Posible extensión de #1488": the car is free for these dates, but
  // an active rental that ends before them was flagged as possibly extending.
  // Warns only; the car stays selectable.
  const [extWarnings, setExtWarnings] = useState({});
  const loadExtensionWarnings = async (cars, fromDate) => {
    const ids = (cars || []).map((c) => c.id);
    if (!ids.length) return;
    const { data } = await supabase
      .from("reservations")
      .select("id, car, date_range")
      .eq("possible_extension", true)
      .eq("status", "active")
      .in("car", ids);
    // Stored ranges are wall clock tagged as UTC: compare the text as-is.
    const from = fromDate.replace("T", " ").substring(0, 16);
    const found = {};
    (data || []).forEach((r) => {
      const end = String(r.date_range).match(/,\s*"?([^")\]]+)"?[)\]]$/)?.[1];
      if (end && end.substring(0, 16) <= from) {
        found[r.car] = `Posible extensión de #${r.id}`;
      }
    });
    setExtWarnings((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (found[id]) next[id] = found[id];
        else delete next[id];
      });
      return next;
    });
  };

  const fetchAvailableCars = async () => {
    const fromRange =
      resDetails.carsToShow === undefined
        ? 0
        : resDetails.carsToShow.length + 1;
    const toRange = fromRange + 4;
    const fromDate = `${resDetails.fromDate} ${resDetails.fromHours || "00:00"}`;
    const toDate = isLP ? null : `${resDetails.toDate} ${resDetails.toHours}`;
    const { data, error } = await supabase
      .rpc("get_available_cars", {
        from_date: fromDate,
        to_date: toDate,
        category_id: resDetails.segmentToSearch,
        search_text: resDetails.searchPhrase?.trim() || "",
        p_branch_id: branch?.id || null,
        p_hold_id: activeHold?.id || null,
      })
      .select()
      .range(fromRange, toRange);

    if (error) {
      console.error(error.message);
      return { success: false, error: error.message };
    }
    loadExtensionWarnings(data, fromDate);
    if (
      resDetails.carsToShow === undefined ||
      resDetails.carsToShow.length === 0
    ) {
      setResDetails({
        ...resDetails,
        carsToShow: data,
        lastResults: data.length < 5,
      });
    } else {
      const newCarsToShow = resDetails.carsToShow.concat(data);
      setResDetails({
        ...resDetails,
        carsToShow: newCarsToShow,
        lastResults: data.length < 5,
      });
    }
  };

  const [editTotal, setEditTotal] = useState({
    activated: false,
    key: undefined,
    value: undefined,
    title: undefined,
  });
  const handleEditTotal = ({ activated, key, value, title }) => {
    setEditTotal({ activated, key, value, title });
  };

  const checkIfReady = () => {
    if (!subleaseInfo && resDetails.selectedCar.car === undefined) return false;
    // A blocked client can't reserve — the insert would be rejected anyway.
    if (blockWarnings.length > 0) return false;
    if (isLP) {
      // LP: company or customer required (from unified search)
      if (!company?.id && !customer?.id) return false;
    } else {
      // CP: customer required
      if (!customer?.id) return false;
    }
    return true;
  };

  const readyToReserve = checkIfReady();

  const createReservation = async () => {
    setSaving(true);
    setConfirmModal(false);

    let resObject;

    if (isLP) {
      // Use the picked start time (form provides fromHours via hoursOfDay select).
      // Fallback to 00:00 only if nothing's set.
      const startTime = (resDetails.fromHours || "00:00").length === 5
        ? `${resDetails.fromHours || "00:00"}:00`
        : (resDetails.fromHours || "00:00:00");
      const startDate = `${resDetails.fromDate} ${startTime}+00`;
      let endDate;
      if (lpIndefinido) {
        endDate = null;
      } else {
        const endMoment = new Date(resDetails.fromDate);
        endMoment.setMonth(endMoment.getMonth() + lpMonths);
        endDate = `${endMoment.toISOString().substring(0, 10)} ${startTime}+00`;
      }

      const details = resDetails.details;
      const totalPrice = details?.totalRent || 0;

      resObject = {
        customer: customer?.id || null,
        company: company?.id || null,
        notes: resNotes || null,
        car: resDetails.selectedCar.car,
        total_price: totalPrice,
        balance: totalPrice,
        deposit: 0,
        rent_time: details?.time || { days: 30, hours: 0 },
        insurance: "base",
        date_range: lpIndefinido
          ? `[${startDate},)`
          : `[${startDate},${endDate}]`,
        totals: { ...details, deposit: 0 },
        created_by: session?.user?.id || null,
        branch_id: branch?.id || 1,
        invoicing_group_id: selectedGroup ? parseInt(selectedGroup) : null,
        price_list_id: resDetails.priceListId,
      };
    } else {
      // Corto Plazo — current behavior
      resObject = {
        customer: customer.id,
        company: company?.id || null,
        booking_origin: bookingOrigin,
        notes: resNotes || null,
        delivery_location_id: deliveryType === "pickup" ? 1 : 2,
        return_location_id: returnType === "pickup" ? 1 : 2,
        delivery: deliveryType === "delivery"
          ? { address: deliveryAddress, type: "domicilio", fee: deliveryFee }
          : { address: "Oficina Central - Colón", type: "sucursal", fee: 0 },
        return: returnType === "delivery"
          ? { address: returnAddress, type: "domicilio", fee: returnFee }
          : { address: "Oficina Central - Colón", type: "sucursal", fee: 0 },
        car: resDetails.selectedCar.car,
        total_price: resDetails.details.totalRent + (deliveryType === "delivery" ? deliveryFee : 0) + (returnType === "delivery" ? returnFee : 0),
        balance: resDetails.details.totalRent + (deliveryType === "delivery" ? deliveryFee : 0) + (returnType === "delivery" ? returnFee : 0),
        deposit: resDetails.details.deposit,
        rent_time: resDetails.details.time,
        insurance: resDetails.insurance,
        date_range: `[${resDetails.fromDate} ${resDetails.fromHours}, ${resDetails.toDate} ${resDetails.toHours}]`,
        totals: resDetails.details,
        created_by: session?.user?.id || null,
        branch_id: branch?.id || 1,
        price_list_id: resDetails.priceListId,
      };
    }
    // Merge sublease info if set — car stays null, we store model_id/plate/vin on the reservation
    if (subleaseInfo) {
      resObject.car = null;
      resObject.is_sublease = true;
      resObject.sublease_model_id = parseInt(subleaseInfo.model_id);
      resObject.sublease_plate = subleaseInfo.plate;
      resObject.sublease_vin = subleaseInfo.vin || null;
    }

    const { data, error } = await supabase
      .from("reservations")
      .insert(resObject)
      .select();
    if (error) {
      console.error(error.message);
      // The blocklist trigger already speaks Spanish and explains the motive —
      // show it verbatim instead of burying it behind a generic prefix.
      const isBlock = /^(Cliente bloqueado|Empresa bloqueada)/.test(
        error.message || ""
      );
      alert(isBlock ? error.message : "Error al crear reserva: " + error.message);
      setSaving(false);
      return;
    }
    if (activeHold) {
      // The reservation now blocks the car; a failure here only leaves the
      // hold to run out on its own.
      const { error: holdErr } = await supabase.rpc("convert_car_hold", {
        p_hold_id: activeHold.id,
        p_reservation_id: data[0].id,
      });
      if (holdErr) console.error(holdErr.message);
    }
    navigate(`/reservation?id=${data[0].id}`);
  };

  // Same range the reservation would get, written as stored wall clock.
  const holdRange = () => {
    const fromTime = (resDetails.fromHours || "00:00").substring(0, 5);
    const from = `${resDetails.fromDate} ${fromTime}:00+00`;
    if (!isLP) return { from, to: `${resDetails.toDate} ${resDetails.toHours}:00+00` };
    if (lpIndefinido) return { from, to: null };
    const end = new Date(resDetails.fromDate);
    end.setMonth(end.getMonth() + lpMonths);
    return { from, to: `${end.toISOString().substring(0, 10)} ${fromTime}:00+00` };
  };

  const canHold =
    !activeHold && !subleaseInfo && resDetails.selectedCar.car != null && !!resDetails.details;

  const createHold = async () => {
    setHoldSaving(true);
    const { from, to } = holdRange();
    // Everything needed to put the form back exactly as it is now.
    const draft = {
      resDetails: { ...resDetails, carsToShow: undefined },
      carRow: resDetails.carsToShow?.find((c) => c.id === resDetails.selectedCar.car) || null,
      deliveryType,
      deliveryAddress,
      deliveryFee,
      returnType,
      returnAddress,
      returnFee,
      bookingOrigin,
      resNotes,
      lpIndefinido,
      lpMonths,
    };
    const { data, error } = await supabase.rpc("create_car_hold", {
      p_car_id: resDetails.selectedCar.car,
      p_from: from,
      p_to: to,
      p_label: holdLabel,
      p_draft: draft,
      p_branch_id: branch?.id || null,
    });
    setHoldSaving(false);
    if (error) {
      alert(error.message);
      return;
    }
    setActiveHold(data);
    setHoldModal(false);
    setHoldLabel("");
    setHoldsRefresh((k) => k + 1);
  };

  const continueHold = (h) => {
    const d = h.draft || {};
    if (!d.resDetails) {
      alert("Este apartado no tiene los datos del formulario.");
      return;
    }
    setSubleaseInfo(null);
    setResDetails({
      ...d.resDetails,
      carsToShow: d.carRow ? [d.carRow] : [],
      lastResults: true,
      missingDates: false,
    });
    if (d.deliveryType) setDeliveryType(d.deliveryType);
    setDeliveryAddress(d.deliveryAddress || "");
    if (d.deliveryFee != null) setDeliveryFee(d.deliveryFee);
    if (d.returnType) setReturnType(d.returnType);
    setReturnAddress(d.returnAddress || "");
    if (d.returnFee != null) setReturnFee(d.returnFee);
    if (d.bookingOrigin) setBookingOrigin(d.bookingOrigin);
    setResNotes(d.resNotes || "");
    if (d.lpIndefinido != null) setLpIndefinido(d.lpIndefinido);
    if (d.lpMonths) setLpMonths(d.lpMonths);
    setActiveHold(h);
    window.scrollTo(0, 0);
  };

  // /reservations/create?hold=<id> (from the timeline) opens that hold.
  const holdParam = searchParams.get("hold");
  useEffect(() => {
    if (!holdParam) return;
    supabase
      .from("car_holds")
      .select(HOLD_SELECT + ", released_at, converted_at")
      .eq("id", holdParam)
      .maybeSingle()
      .then(({ data }) => {
        if (isHoldLive(data)) continueHold(data);
        else if (data) alert("Ese apartado ya venció, se liberó o se convirtió en reserva.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdParam]);

  return (
    <AppWrapper>
      <Page
        title="Crear Reserva"
        backAction={{ content: "Reservations", url: "/reservations" }}
      >
        <PopUpAmount
          title={editTotal.title}
          activated={editTotal.activated}
          ogValue={editTotal.value}
          passInfo={({ activated, key, value, title }) => {
            handleEditTotal({ activated, key, value, title });
          }}
          save={(e) => {
            const newValue = parseInt(e);
            const key = editTotal.key;
            var dailyPrice =
              key === "dailyPrice" ? newValue : resDetails.details.dailyPrice;
            var deposit =
              key === "deposit" ? newValue : resDetails.details.deposit;
            var totalRent =
              key === "totalRent" ? newValue : resDetails.details.totalRent;
            var hoursExtra =
              key === "hoursExtra" ? newValue : resDetails.details.hoursExtra;

            const { days, hours } = resDetails.details.time;

            if (key === "dailyPrice") {
              totalRent = dailyPrice * days + hoursExtra;
            } else if (key === "hoursExtra") {
              totalRent = dailyPrice * days + hoursExtra;
            } else if (key === "totalRent") {
              dailyPrice = totalRent / days;
              hoursExtra = 0;
            }

            setResDetails({
              ...resDetails,
              details: {
                ...resDetails.details,
                dailyPrice,
                deposit,
                totalRent,
                hoursExtra,
              },
            });
            handleEditTotal({
              activated: false,
              key: undefined,
              value: undefined,
              title: undefined,
            });
          }}
        />
        <PopUpSearch
          activated={customerModal}
          setOpen={handleCustomerModal}
          getCustomer={({ id, name, email, phone }) => {
            setCustomer({
              id: id,
              name: name,
              contact: email || phone,
            });
          }}
          title="Clientes"
          dbFunction="search_customers"
        />
        <PopUpSearch
          activated={companyModal}
          setOpen={handleCompanyModal}
          getCustomer={({ id, name }) => selectCompany({ id, name })}
          title="Empresa"
          dbFunction="search_company"
        />
        <Modal
          open={subleaseModal}
          onClose={() => setSubleaseModal(false)}
          title="Auto Subarrendado"
          primaryAction={{
            content: "Seleccionar",
            onAction: confirmSublease,
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setSubleaseModal(false) }]}
        >
          <Modal.Section>
            <FormLayout>
              <Select
                label="Modelo"
                value={subleaseData.model_id}
                options={[
                  { label: "Seleccionar un modelo", value: "" },
                  ...carModels.map((m) => ({
                    label: `${m.brand?.name || ""} ${m.show_as || m.name}`.trim(),
                    value: String(m.id),
                  })),
                ]}
                onChange={(val) => setSubleaseData({ ...subleaseData, model_id: val })}
                requiredIndicator
                helpText="Selecciona el modelo más cercano de nuestro catálogo"
              />
              <FormLayout.Group>
                <TextField
                  label="Placa"
                  value={subleaseData.plate}
                  onChange={(val) => setSubleaseData({ ...subleaseData, plate: val.toUpperCase() })}
                  autoComplete="off"
                  requiredIndicator
                />
                <TextField
                  label="VIN (opcional)"
                  value={subleaseData.vin}
                  onChange={(val) => setSubleaseData({ ...subleaseData, vin: val.toUpperCase() })}
                  autoComplete="off"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField
                  label="Precio diario"
                  type="number"
                  prefix="$"
                  value={subleaseData.dailyPrice}
                  onChange={(val) => setSubleaseData({ ...subleaseData, dailyPrice: val })}
                  autoComplete="off"
                />
                {!isLP && (
                  <TextField
                    label="Depósito"
                    type="number"
                    prefix="$"
                    value={subleaseData.deposit}
                    onChange={(val) => setSubleaseData({ ...subleaseData, deposit: val })}
                    autoComplete="off"
                  />
                )}
              </FormLayout.Group>
            </FormLayout>
          </Modal.Section>
        </Modal>
        <ClientSearch
          open={clientSearchModal}
          onClose={() => setClientSearchModal(false)}
          onSelect={(r) => {
            if (r.client_type === "company") {
              selectCompany({ id: r.id, name: r.name });
            } else {
              setCustomer({ id: r.id, name: r.name });
              setCompany({ id: undefined, name: undefined });
            }
            setClientSearchModal(false);
          }}
        />
        <Modal
          open={holdModal}
          onClose={() => setHoldModal(false)}
          title={`Apartar por ${holdHours} horas hábiles`}
          primaryAction={{
            content: "Apartar",
            onAction: createHold,
            loading: holdSaving,
            disabled: holdSaving,
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setHoldModal(false) }]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text>
                El auto queda bloqueado para estas fechas durante {holdHours} horas hábiles
                (horario de la empresa). Si no se crea la reserva, se libera solo.
              </Text>
              <TextField
                label="¿Para quién? (opcional)"
                value={holdLabel}
                onChange={setHoldLabel}
                autoComplete="off"
                placeholder="Nombre o teléfono del prospecto"
              />
            </BlockStack>
          </Modal.Section>
        </Modal>
        <Modal
          open={confirmModal}
          onClose={() => setConfirmModal(false)}
          title="Confirmar Reserva"
          primaryAction={{
            content: "Confirmar",
            onAction: createReservation,
          }}
          secondaryActions={[
            { content: "Cancelar", onAction: () => setConfirmModal(false) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="200">
              <Text>
                <Text fontWeight="semibold" as="span">Cliente:</Text>{" "}
                {customer.name || "-"}
              </Text>
              {company?.name && (
                <Text>
                  <Text fontWeight="semibold" as="span">Empresa:</Text>{" "}
                  {company.name}
                </Text>
              )}
              <Text>
                <Text fontWeight="semibold" as="span">Fechas:</Text>{" "}
                {resDetails.fromDate} {resDetails.fromHours} →{" "}
                {resDetails.toDate} {resDetails.toHours}
              </Text>
              <Text>
                <Text fontWeight="semibold" as="span">Entrega:</Text>{" "}
                {deliveryType === "pickup" ? "Recoger en sucursal" : `Domicilio: ${deliveryAddress}`}
              </Text>
              <Text>
                <Text fontWeight="semibold" as="span">Devolución:</Text>{" "}
                {returnType === "pickup" ? "Devolver en sucursal" : `Domicilio: ${returnAddress}`}
              </Text>
              {resDetails.details && (
                <>
                  <Text>
                    <Text fontWeight="semibold" as="span">Total Renta:</Text>{" "}
                    ${resDetails.details.totalRent?.toLocaleString()}
                  </Text>
                  <Text>
                    <Text fontWeight="semibold" as="span">Depósito:</Text>{" "}
                    ${resDetails.details.deposit?.toLocaleString()}
                  </Text>
                </>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
        <div style={{ marginBottom: "1rem" }}>
          <CarHolds
            branchId={branch?.id}
            userId={session?.user?.id}
            refreshKey={holdsRefresh}
            activeHoldId={activeHold?.id}
            onContinue={continueHold}
            onChanged={(h) => {
              if (!h || h.id !== activeHold?.id) return;
              setActiveHold(h.released_at ? null : (prev) => ({ ...prev, ...h }));
            }}
          />
        </div>
        <Layout>
          <Layout.Section>
            {activeHold && (
              <div style={{ marginBottom: "1rem" }}>
                {isHoldLive(activeHold) ? (
                  <Banner
                    tone="info"
                    title={`Auto apartado${activeHold.label ? ` para ${activeHold.label}` : ""}${activeHold.seller?.name ? ` por ${activeHold.seller.name}` : ""} · vence ${holdTime(activeHold.expires_at)} (${timeLeft(activeHold)})`}
                  >
                    <Text>Cuando lleguen los documentos, elige al cliente y crea la reserva.</Text>
                  </Banner>
                ) : (
                  <Banner tone="warning" title="El apartado venció" onDismiss={() => setActiveHold(null)}>
                    <Text>El auto ya no está bloqueado; búscalo de nuevo antes de crear la reserva.</Text>
                  </Banner>
                )}
              </div>
            )}
            <FormLayout>
              <FormLayout.Group condensed>
                <TextField
                  label={isLP ? "Fecha de Inicio" : "Fecha de Entrega"}
                  type="date"
                  value={resDetails.fromDate}
                  id="fromDate"
                  onChange={(value, id) => {
                    handleChange(value, id);
                  }}
                  error={
                    resDetails.missingDates
                      ? resDetails.fromDate === ""
                        ? "Fecha faltante"
                        : undefined
                      : undefined
                  }
                />
                <Select
                  label="‎"
                  options={hoursOfDay}
                  value={resDetails.fromHours}
                  id="fromHours"
                  onChange={(value, id) => {
                    handleChange(value, id);
                  }}
                />
                {!isLP && (
                <TextField
                  label="Fecha de Devolución"
                  type="date"
                  min={resDetails.fromDate}
                  value={resDetails.toDate}
                  id="toDate"
                  onChange={(value, id) => {
                    handleChange(value, id);
                  }}
                  error={
                    resDetails.missingDates
                      ? resDetails.toDate === ""
                        ? "Fecha faltante"
                        : undefined
                      : undefined
                  }
                />
                )}
                {!isLP && (
                <Select
                  label="‎"
                  options={hoursOfDay}
                  value={resDetails.toHours}
                  id="toHours"
                  onChange={(value, id) => {
                    handleChange(value, id);
                  }}
                />
                )}
              </FormLayout.Group>
              {!isLP && (
              <FormLayout.Group condensed>
                <Select
                  label="Licencia"
                  options={[
                    { label: "Local", value: "local" },
                    { label: "Foránea", value: "foreign" },
                  ]}
                  value={resDetails.license}
                  id="license"
                  onChange={(value, id) => {
                    if (value === "local") {
                      handleChange(value, id);
                    } else {
                      setResDetails({
                        ...resDetails,
                        license: value,
                        paymentMethod: "card",
                        insurance: "premium",
                        carsToShow: undefined,
                        selectedCar: {
                          model: undefined,
                          car: undefined,
                        },
                        details: undefined,
                      });
                    }
                  }}
                />
                <Select
                  label="Pago"
                  options={[
                    { label: "Tarjeta de Crédito", value: "card" },
                    { label: "Efectivo", value: "cash" },
                  ]}
                  value={resDetails.paymentMethod}
                  id="paymentMethod"
                  onChange={(value, id) => {
                    handleChange(value, id);
                  }}
                  disabled={resDetails.license === "foreign"}
                />
                <Select
                  label="Seguro"
                  options={[
                    { label: "Premium 0%", value: "premium" },
                    { label: "Standard 0%", value: "standard" },
                    { label: "Básico 10%", value: "base" },
                  ]}
                  value={resDetails.insurance}
                  id="insurance"
                  onChange={(value, id) => {
                    handleChange(value, id);
                  }}
                  disabled={resDetails.license === "foreign"}
                />
              </FormLayout.Group>
              )}
              {isLP && (
              <>
              <FormLayout.Group condensed>
                <Select
                  label="Duración"
                  value={lpIndefinido ? "indefinido" : "fijo"}
                  options={[
                    { label: "Indefinido", value: "indefinido" },
                    { label: "Plazo fijo", value: "fijo" },
                  ]}
                  onChange={(val) => setLpIndefinido(val === "indefinido")}
                />
                {!lpIndefinido && (
                  <TextField
                    label="Meses"
                    type="number"
                    value={String(lpMonths)}
                    onChange={(val) => setLpMonths(parseInt(val) || 1)}
                    min={1}
                  />
                )}
                <Select
                  label="Grupo de Facturación"
                  value={selectedGroup}
                  options={[
                    { label: "Sin grupo", value: "" },
                    ...invoicingGroups.map(g => ({ label: g.name, value: String(g.id) })),
                  ]}
                  onChange={setSelectedGroup}
                />
              </FormLayout.Group>
              </>
              )}
              <FormLayout.Group condensed>
                <InlineGrid columns={["oneThird", "twoThirds"]} gap={300}>
                  <Select
                    label="Segmento"
                    options={[
                      { label: "Todos", value: 0 },
                      { label: "Sedán", value: 1 },
                      { label: "Carga", value: 2 },
                      { label: "Pasajeros", value: 3 },
                    ]}
                    style={{ maxWidth: "4rem" }}
                    id="segmentToSearch"
                    value={resDetails.segmentToSearch}
                    onChange={(value, id) => {
                      handleChange(value, id);
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "12px",
                      alignItems: "end",
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      <TextField
                        label="Buscar"
                        placeholder="ejemplo: Estaca, Toyota, Hiace etc"
                        id="searchPhrase"
                        value={resDetails.searchPhrase}
                        onChange={(selected, id) => {
                          handleChange(selected, id);
                        }}
                      />
                    </span>

                    <span
                      style={{
                        width: "32px",
                        height: "32px",
                        aspectRatio: "1",
                      }}
                    >
                      <Button
                        icon={SearchIcon}
                        accessibilityLabel="Buscar"
                        size="large"
                        disabled={resDetails.carsToShow}
                        onClick={() => {
                          if (
                            resDetails.fromDate === "" ||
                            (!isLP && resDetails.toDate === "")
                          ) {
                            setResDetails({
                              ...resDetails,
                              missingDates: true,
                            });
                          } else {
                            const availableCarsContainer =
                              document.getElementById("availableCarsContainer");
                            availableCarsContainer.scrollTo(0, 0);
                            fetchAvailableCars();
                          }
                        }}
                      />
                    </span>
                  </div>
                </InlineGrid>
              </FormLayout.Group>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text variant="bodyMd">Vehículos Disponibles</Text>
                {(() => {
                  const hasSublease = !!subleaseInfo;
                  const hasFleetCar =
                    resDetails.selectedCar.car !== undefined &&
                    resDetails.selectedCar.car !== null;
                  if (hasFleetCar && !hasSublease) {
                    return (
                      <Button
                        disabled
                        helpText="Ya seleccionaste un auto"
                      >
                        Subarrendado
                      </Button>
                    );
                  }
                  return (
                    <Button
                      onClick={() => {
                        if (hasSublease) {
                          // Pre-fill modal with existing sublease info for editing
                          setSubleaseData({
                            model_id: subleaseInfo.model_id || "",
                            plate: subleaseInfo.plate || "",
                            vin: subleaseInfo.vin || "",
                            dailyPrice: String(subleaseInfo.dailyPrice || "1000"),
                            deposit: String(subleaseInfo.deposit ?? "4000"),
                          });
                        } else {
                          setSubleaseData({
                            model_id: "",
                            plate: "",
                            vin: "",
                            dailyPrice: "1000",
                          });
                        }
                        setSubleaseModal(true);
                      }}
                    >
                      {hasSublease ? "Editar Subarrendado" : "Subarrendado"}
                    </Button>
                  );
                })()}
              </div>
              {subleaseInfo && (
                <Card>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <BlockStack gap="100">
                      <Text variant="headingSm">
                        <Badge tone="info">Subarrendado</Badge> {subleaseInfo.model_label}
                      </Text>
                      <Text variant="bodySm" tone="subdued">
                        Placa: {subleaseInfo.plate}
                        {subleaseInfo.vin ? ` · VIN: ${subleaseInfo.vin}` : ""}
                        {" · "}Precio diario: ${parseFloat(subleaseInfo.dailyPrice).toLocaleString()}
                      </Text>
                    </BlockStack>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => {
                        setSubleaseInfo(null);
                        setResDetails({
                          ...resDetails,
                          selectedCar: { car: undefined, model: undefined },
                          details: undefined,
                        });
                      }}
                    >
                      Quitar
                    </Button>
                  </div>
                </Card>
              )}
              <div
                className="create_reservation_available_cars_container"
                id="availableCarsContainer"
              >
                {resDetails.carsToShow ? (
                  getCarCards()
                ) : (
                  <>
                    <CarCard loading /> <CarCard loading /> <CarCard loading />
                  </>
                )}
              </div>
            </FormLayout>
            <FormLayout.Group condensed>
              {isLP ? (
                <TextField
                  label="Cliente"
                  onFocus={() => setClientSearchModal(true)}
                  value={company?.name || customer?.name || ""}
                  requiredIndicator
                  placeholder="Buscar empresa o persona"
                />
              ) : (
                <>
                  <TextField
                    label="Empresa (opcional)"
                    onFocus={() => setCompanyModal(true)}
                    value={company.name}
                  />
                  <TextField
                    label="Cliente"
                    onFocus={() => setCustomerModal(true)}
                    value={customer.name}
                    requiredIndicator
                  />
                </>
              )}
            </FormLayout.Group>
            {blockWarnings.length > 0 && (
              <Banner
                title="No se puede crear la reservación"
                tone="critical"
              >
                <BlockStack gap="100">
                  {blockWarnings.map((msg) => (
                    <Text key={msg}>{msg}</Text>
                  ))}
                  <Text tone="subdued" variant="bodySm">
                    Desbloquea al cliente en su ficha o selecciona otro.
                  </Text>
                </BlockStack>
              </Banner>
            )}
            {!isLP && (
            <FormLayout.Group condensed>
              <Select
                label="Entrega"
                value={deliveryType}
                options={[
                  { label: "Recoger en sucursal", value: "pickup" },
                  { label: "Entrega a domicilio", value: "delivery" },
                ]}
                onChange={setDeliveryType}
              />
              {deliveryType === "delivery" && (
                <TextField
                  label="Dirección de entrega"
                  value={deliveryAddress}
                  onChange={setDeliveryAddress}
                  placeholder="Calle, colonia, ciudad"
                />
              )}
              <Select
                label="Devolución"
                value={returnType}
                options={[
                  { label: "Devolver en sucursal", value: "pickup" },
                  { label: "Recoger a domicilio", value: "delivery" },
                ]}
                onChange={setReturnType}
              />
              {returnType === "delivery" && (
                <TextField
                  label="Dirección de devolución"
                  value={returnAddress}
                  onChange={setReturnAddress}
                  placeholder="Calle, colonia, ciudad"
                />
              )}
            </FormLayout.Group>
            )}
            <TextField
              label="Notas"
              value={resNotes}
              onChange={setResNotes}
              multiline={2}
            />
          </Layout.Section>

          <Layout.Section
            variant="oneThird"
            className="create_reservation_totals"
          >
            <BlockStack gap={300}>
              {priceLists.length > 1 && (
                <Card>
                  <Select
                    label="Lista de precios"
                    options={priceLists.map((l) => ({
                      label: `${l.title}${l.is_public ? " (Pública)" : ""}`,
                      value: String(l.id),
                    }))}
                    value={resDetails.priceListId ? String(resDetails.priceListId) : ""}
                    id="priceListId"
                    onChange={(value) => handlePriceListChange(value)}
                    helpText="Cambiar la lista recalcula el total con el auto seleccionado."
                  />
                </Card>
              )}
              {isLP ? (
                <Card>
                  <BlockStack gap="200">
                    <Text variant="headingMd">Resumen Largo Plazo</Text>
                    {resDetails.details ? (
                      <>
                        <InlineGrid columns="1fr auto">
                          <Text>Tarifa Diaria</Text>
                          <Button variant="plain" onClick={() => handleEditTotal({ activated: true, key: "dailyPrice", value: resDetails.details.dailyPrice, title: "Tarifa Diaria" })}>
                            {formatPrice(resDetails.details.dailyPrice)}
                          </Button>
                        </InlineGrid>
                        <InlineGrid columns="1fr auto">
                          <Text>Duración</Text>
                          <Text>{lpIndefinido ? "Indefinido" : `${lpMonths} meses`}</Text>
                        </InlineGrid>
                        <InlineGrid columns="1fr auto">
                          <Text tone="subdued">Estimado mensual</Text>
                          <Text tone="subdued">{formatPrice(resDetails.details.dailyPrice * 30)}</Text>
                        </InlineGrid>
                        <Divider />
                        {!lpIndefinido && (
                        <InlineGrid columns="1fr auto">
                          <Text fontWeight="bold">Total estimado</Text>
                          <Button variant="plain" onClick={() => handleEditTotal({ activated: true, key: "totalRent", value: resDetails.details.totalRent, title: "Total" })}>
                            <Text fontWeight="bold">{formatPrice(resDetails.details.totalRent)}</Text>
                          </Button>
                        </InlineGrid>
                        )}
                        <InlineGrid columns="1fr auto">
                          <Text>Depósito</Text>
                          <Button variant="plain" onClick={() => handleEditTotal({ activated: true, key: "deposit", value: resDetails.details.deposit, title: "Depósito" })}>
                            {formatPrice(resDetails.details.deposit)}
                          </Button>
                        </InlineGrid>
                        {selectedGroup && invoicingGroups.length > 0 && (
                          <InlineGrid columns="1fr auto">
                            <Text tone="subdued">Grupo</Text>
                            <Text tone="subdued">{invoicingGroups.find(g => String(g.id) === selectedGroup)?.name || "-"}</Text>
                          </InlineGrid>
                        )}
                      </>
                    ) : (
                      <Text tone="subdued">Selecciona un vehículo</Text>
                    )}
                  </BlockStack>
                </Card>
              ) : (
              <CreateReservationSummary
                details={resDetails}
                editTotal={handleEditTotal}
                deliveryType={deliveryType}
                deliveryFee={deliveryFee}
                onDeliveryFeeChange={setDeliveryFee}
                returnType={returnType}
                returnFee={returnFee}
                onReturnFeeChange={setReturnFee}
              />
              )}
              {/* Car + dates but no customer yet: the same button holds the car. */}
              {!readyToReserve && canHold ? (
                <Button fullWidth variant="primary" onClick={() => setHoldModal(true)}>
                  {`Apartar por ${holdHours} horas hábiles`}
                </Button>
              ) : (
                <Button
                  fullWidth
                  style={{ marin: "1rem" }}
                  tone="undefined"
                  disabled={!readyToReserve || saving}
                  loading={saving}
                  onClick={() => setConfirmModal(true)}
                >
                  Crear Reserva
                </Button>
              )}
            </BlockStack>
          </Layout.Section>
        </Layout>
      </Page>
    </AppWrapper>
  );
}

export default CreateReservation;

/*

Tarifa Diaria   $1,270
Total           $6,350
Depósito        $4,000
Total           $10,350



*/
